import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './FlightCarousel.css';

/**
 * FlightCarousel component for displaying multiple flights in a rotating carousel
 * Only the Flight icon and flight number roll, while DESTINATION/ORIGIN, TIME, ESTM, TERMINAL, STATUS stay fixed
 */
const FlightCarousel = ({ flights, delay = 3000, index = 0, type = 'arrivals' }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (flights.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % flights.length);
    }, delay);

    return () => clearInterval(interval);
  }, [flights.length, delay]);

  if (flights.length === 0) return null;
  if (flights.length === 1) {
    // If only one flight, render it normally
    const flight = flights[0];
    return (
      <div className="row-content">
        <div className="col-flight">
          <div className="flight-header">
            {flight.logoPath && (
              <img 
                src={`/logos/${flight.logoPath.split('/').pop()}`} 
                alt={flight.airlineCode}
                className="airline-logo"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            )}
            {!flight.logoPath && flight.logoUrl && (
              <img 
                src={flight.logoUrl} 
                alt={flight.airlineCode}
                className="airline-logo"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            )}
            <div className="flight-info">
              <div className="flight-number">{flight.flightNumber}</div>
              <div className="airline-code">{flight.airlineCode}</div>
            </div>
          </div>
        </div>
        <div className="col-origin">
          <div className="origin-text">
            {type === 'departures' 
              ? (flight.airlineCode === 'Other' || !flight.airlineCode ? 'N/A' : (flight.destination || 'N/A'))
              : (flight.origin || 'N/A')
            }
          </div>
        </div>
        <div className="col-time">
          <div className="time-text">{flight.scheduledTime || '--:--'}</div>
        </div>
        <div className="col-estimated">
          <div className="estimated-text">
            {flight.estimatedTime || flight.scheduledTime || '--:--'}
          </div>
        </div>
        <div className="col-terminal">
          <div className="terminal-badge">{flight.terminal || '--'}</div>
        </div>
        <div className="col-status">
          <div className="status-badge" style={{ backgroundColor: '#94a3b8', color: '#ffffff' }}>
            {flight.status || 'SCHEDULED'}
          </div>
        </div>
      </div>
    );
  }

  // Use the first flight's data for fixed columns (they should be the same for grouped flights)
  const baseFlight = flights[0];

  // Get status color and icon
  const getStatusColor = (status) => {
    const statusUpper = status?.toUpperCase() || '';
    if (statusUpper.includes('CANCELLED')) return '#ef4444';
    if (statusUpper.includes('FINAL CALL')) return '#ef4444'; // Red - urgent attention, blinking
    if (statusUpper.includes('DELAYED')) return '#f59e0b';
    if (statusUpper.includes('GATE CLOSED')) return '#f97316';
    if (statusUpper.includes('CHECK-IN CLOSED')) return '#ec4899';
    if (statusUpper.includes('BOARDING')) return '#1e40af';
    if (statusUpper.includes('DEPARTED')) return '#9333ea';
    if (statusUpper.includes('LANDED')) return '#10b981';
    if (statusUpper.includes('GATE')) return '#8b5cf6';
    return '#94a3b8';
  };

  const getStatusIcon = (status) => {
    const statusUpper = status?.toUpperCase() || '';
    if (statusUpper.includes('LANDED')) return '✓';
    if (statusUpper.includes('DELAYED')) return '⏱';
    if (statusUpper.includes('CANCELLED')) return '✕';
    if (statusUpper.includes('BOARDING')) return '';
    if (statusUpper.includes('GATE CLOSED')) return '';
    if (statusUpper.includes('CHECK-IN CLOSED')) return '';
    if (statusUpper.includes('DEPARTED')) return '✈️';
    if (statusUpper.includes('GATE')) return '📍';
    return '○';
  };

  const statusColor = getStatusColor(baseFlight.status);
  const statusIcon = getStatusIcon(baseFlight.status);
  const isFinalCall = baseFlight.status?.toUpperCase().includes('FINAL CALL');
  const isBoarding = baseFlight.status?.toUpperCase().includes('BOARDING');

  return (
    <motion.div 
      className="row-content carousel-row"
      animate={isFinalCall ? {
        backgroundColor: [
          'rgba(239, 68, 68, 0.05)',
          'rgba(239, 68, 68, 0.15)',
          'rgba(239, 68, 68, 0.05)'
        ]
      } : {}}
      transition={{
        duration: 0.8,
        repeat: isFinalCall ? Infinity : 0,
        ease: "easeInOut"
      }}
    >
      {/* Rolling column: Flight */}
      <div className="col-flight carousel-col">
          <div className="carousel-roll-container">
            <AnimatePresence mode="sync">
              {flights.map((flight, idx) => {
                if (idx !== currentIndex) return null;
                return (
                  <motion.div
                    key={`flight-${idx}`}
                    initial={{ y: '100%', opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: '-100%', opacity: 0 }}
                    transition={{ duration: 0.6, ease: "easeInOut" }}
                    className="carousel-roll-item"
                  >
                    <div className="flight-header">
                      {flight.logoPath && (
                        <img 
                          src={`/logos/${flight.logoPath.split('/').pop()}`} 
                          alt={flight.airlineCode}
                          className="airline-logo"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      )}
                      {!flight.logoPath && flight.logoUrl && (
                        <img 
                          src={flight.logoUrl} 
                          alt={flight.airlineCode}
                          className="airline-logo"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      )}
                      <div className="flight-info">
                        <div className="flight-number">{flight.flightNumber}</div>
                        <div className="airline-code">{flight.airlineCode}</div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        </div>
      
      {/* Fixed column: Destination/Origin (no roll effect) */}
      <div className="col-origin">
        <div className="origin-text">
          {type === 'departures' 
            ? (baseFlight.airlineCode === 'Other' || !baseFlight.airlineCode ? 'N/A' : (baseFlight.destination || 'N/A'))
            : (baseFlight.origin || 'N/A')
          }
        </div>
      </div>

      {/* Fixed columns: Time, ESTM, Terminal, Status */}
      <div className="col-time">
        <div className="time-text">{baseFlight.scheduledTime || '--:--'}</div>
      </div>
      
      <div className="col-estimated">
        <div className="estimated-text">
          {baseFlight.estimatedTime || baseFlight.scheduledTime || '--:--'}
        </div>
      </div>
      
      <div className="col-terminal">
        <div className="terminal-badge">{baseFlight.terminal || '--'}</div>
      </div>
      
      <div className="col-status">
        <div className="status-badge-wrapper">
          <motion.div 
            className="status-badge"
            style={{ 
              backgroundColor: statusColor,
              color: '#ffffff',
              fontWeight: '700',
              boxShadow: `0 2px 8px ${statusColor}40`
            }}
            animate={isFinalCall ? {
              opacity: [1, 0.5, 1],
              boxShadow: [
                `0 2px 8px ${statusColor}40`,
                `0 4px 16px ${statusColor}80`,
                `0 2px 8px ${statusColor}40`
              ]
            } : isBoarding ? {
              scale: [1, 1.05, 1],
              boxShadow: [
                `0 2px 8px ${statusColor}40`,
                `0 4px 20px ${statusColor}90`,
                `0 2px 8px ${statusColor}40`
              ]
            } : {}}
            transition={{
              duration: isFinalCall ? 0.8 : isBoarding ? 1.2 : 2,
              repeat: isFinalCall || isBoarding ? Infinity : 0,
              ease: "easeInOut"
            }}
          >
            <span className="status-icon">{statusIcon}</span>
            <span className="status-text">{baseFlight.status || 'SCHEDULED'}</span>
          </motion.div>
          {/* Indicator as separate element below status badge */}
          {flights.length > 1 && (
            <div className="flight-carousel-indicator-badge">
              {currentIndex + 1}/{flights.length}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default FlightCarousel;
